/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { useState, useContext, Fragment as _, useCallback } from 'react';
import { interactionContext as InteractionContext } from './interactionContext';

import { CertificateContext } from './Certificate';
import { Certificate_1_SVG } from './Certificate_1_SVG';
import { Certificate_2_SVG } from './Certificate_2_SVG';
import { Certificate_3_SVG } from './Certificate_3_SVG';
import chameleonLogoForCertificate from 'assets/chameleonLogoForCertificate.svg';
import { InputText } from './InputText';

// https://react-svgr.com/playground/

const learnerNameGrey = '#A1ADAD';

const CertificateTemplateFields = props => {
  const {
    id,
    scaleFactor,
    inverseScaleFactor,
    color,
    date,
    courseName,
    fontSize,
    coordinates,
  } = props;

  const certificatePadding = 0;
  const offset = 12;

  if (!coordinates) {
    return null;
  }

  return (
    <_>
      <div
        style={{
          position: 'absolute',
          left:
            (coordinates?.courseName?.[0] - certificatePadding) * scaleFactor,
          top:
            (coordinates?.courseName?.[1] -
              certificatePadding * 2 -
              (fontSize + offset)) *
            scaleFactor,
          height: 0,
          transform: `scale(${scaleFactor})`,
          transformOrigin: 'bottom left',
        }}
      >
        {courseName}
      </div>
      <div
        style={{
          position: 'absolute',
          left:
            (coordinates?.learnerName?.[0] - certificatePadding) * scaleFactor,
          top:
            (coordinates?.learnerName?.[1] -
              certificatePadding * 2 -
              (fontSize + offset)) *
            scaleFactor,
          height: 0,
          transform: `scale(${scaleFactor})`,
          transformOrigin: 'bottom left',
        }}
      >
        <LearnerName
          width={
            coordinates?.learnerName?.[2] * scaleFactor * inverseScaleFactor
          }
        />
      </div>

      <div
        style={{
          position: 'absolute',
          left: (coordinates?.date?.[0] - certificatePadding) * scaleFactor,
          top:
            (coordinates?.date?.[1] -
              certificatePadding * 2 -
              (fontSize + offset)) *
            scaleFactor,
          height: 0,
          transform: `scale(${scaleFactor})`,
          transformOrigin: 'bottom left',
        }}
      >
        {date}
      </div>
    </_>
  );
};

const LearnerName = props => {
  const { width } = props;

  return window.dev() ? (
    <span
      style={{
        color: learnerNameGrey,
      }}
    >
      Learner inputs name here
    </span>
  ) : (
    <InputText
      {...{
        width,
        placeholder: 'Enter your name here',
        focusColor: learnerNameGrey,
      }}
    />
  );
};

export const CertificateTemplate = props => {
  const interactionContext = useContext(InteractionContext);
  const certificateContext = useContext(CertificateContext);

  const { id } = interactionContext;

  const {
    certificateTemplateIndex = 0,
    certificateTemplateColor: color,
    certificateTemplateTitle: courseName = window.dev()
      ? `*Add a Course name in the Certificate settings`
      : '',
    generatingPDF,
  } = interactionContext.props;

  const { src: logo = chameleonLogoForCertificate } = props;

  const { scaleFactor, inverseScaleFactor } = certificateContext;

  const dateArray = new Date().toString().split(/ /g);
  const date = dateArray.slice(0, 4).join(' ');

  const [SVG, setSVG] = useState<string | undefined>('true'); //
  const [coordinates, setCoordinates] = useState<{} | undefined>();

  const logoCoords = {
    0: {
      left: '50%',
      top: 115,
      transform: 'translateX(-50%)',
    },

    1: {
      left: 255,
      top: 80,
    },

    2: {
      right: 80,
      top: 75,
    },
  }[certificateTemplateIndex];

  const fontSize = 32;

  const svgContainer = useCallback(
    svg => {
      if (!svg) {
        return;
      }

      const learnerNameElement = svg.querySelector('#Learner_Name rect');
      const courseNameElement = svg.querySelector('#Course_Name rect');
      const dateElement = svg.querySelector('#Date rect');

      const changeColorElement = svg.querySelector('#Change_colour');

      if (learnerNameElement && courseNameElement && dateElement) {
        if (window.dev()) {
          learnerNameElement.style.fill = learnerNameGrey;
        }

        setCoordinates({
          learnerName: [
            learnerNameElement.getAttribute('x'),
            learnerNameElement.getAttribute('y'),
            learnerNameElement.getAttribute('width'),
          ],
          courseName: [
            courseNameElement.getAttribute('x'),
            courseNameElement.getAttribute('y'),
          ],
          date: [dateElement.getAttribute('x'), dateElement.getAttribute('y')],
        });
      }

      if (!changeColorElement) {
        return;
      }

      const changeColorElements = Array.from(
        changeColorElement.querySelectorAll('[style*="fill"]')
      ) as (HTMLElement & SVGElement)[];

      changeColorElements?.forEach(element => {
        element.style.fill = color;
      });
    },
    [SVG, certificateTemplateIndex, color]
  );

  if (!SVG) {
    return null;
  }

  return (
    <div
      key={`${certificateTemplateIndex}-${color}`}
      css={css({
        position: 'relative',
        svg: {
          boxShadow: generatingPDF ? 'none' : '0 0 10px rgba(0,0,0,0.1)',
        },
      })}
    >
      <div
        ref={svgContainer}
        key={`${certificateTemplateIndex}-${color}`}
        id={`svg-container-${id}`}
      >
        {
          [<Certificate_1_SVG />, <Certificate_2_SVG />, <Certificate_3_SVG />][
            certificateTemplateIndex
          ]
        }
      </div>

      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          fontSize,
          fontWeight: 500,
        }}
      >
        <CertificateTemplateFields
          key={`certificate-template-fields-${certificateTemplateIndex}`}
          {...{
            scaleFactor,
            inverseScaleFactor,
            color,
            fontSize,
            courseName,
            date,
            coordinates,
          }}
        />
      </div>
      <div
        ref={node => {
          if (!node) {
            return;
          }

          const logoScale = node.offsetWidth / 1100;

          const img = node.firstChild as HTMLImageElement;
          if (img) {
            img.style.width = `${80 * logoScale}px`;
            img.style.top = `${logoCoords.top * logoScale}px`;

            const leftRight = logoCoords.left ? 'left' : 'right';
            img.style[leftRight] = `${logoCoords[leftRight] * logoScale}px`;
          }
        }}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          transformOrigin: '0px 0px',
          pointerEvents: 'none',
        }}
      >
        <img
          src={logo}
          style={{
            position: 'absolute',
            ...logoCoords,
            width: 80,
          }}
        />
      </div>
    </div>
  );
};

const CertificateTemplates = props => {
  const interactionContext = useContext(InteractionContext);

  const { certificateTemplateIndex = 0 } = interactionContext.props;

  const requiredVariables = [['learnerName'], [], []][certificateTemplateIndex];

  const moduleVariables = [''];

  if (requiredVariables.every(entry => moduleVariables.includes(entry))) {
    console.log('certificate template has the required variables !!!');
  } else {
    console.log('certificate template DOES NOT has the required variables !!!');
  }

  return <CertificateTemplate {...props} />;
};

export default CertificateTemplates;
